import React, { useState, useEffect } from 'react';
import { connect } from 'react-redux';
import { Helmet } from 'react-helmet-async';
import {
  archiveUnit,
  unArchiveUnit,
  loadUnit,
} from '../../redux/actions/unitActions';
import { FormattedMessage } from 'react-intl';
import { Link, useLocation, useParams, useNavigate } from 'react-router-dom';
import Button, {
  buttonTypes,
  buttonSizes,
  buttonTextSizes,
} from '../shared/Button';
import Overlay from '../shared/Overlay';
import SvgUpArrowIcon from '../svg/UpArrowIcon';
import {
  cancelText,
  editText,
  archiveText,
  unArchiveText,
  archiveChallengeText,
  unArchiveChallengeText,
  infoText,
  checkinText,
  devicesText,
} from '../shared/SharedInternationalizations';
import { loadAlarmDeviceTypes } from '../../redux/actions/alarmDeviceActions';
import { loadAlertDeviceTypes } from '../../redux/actions/alertDeviceActions';
import UnitInfo from './unitDisplay/UnitInfo';
import UnitDevices from './unitDisplay/UnitDevices';
import UnitCheckinPreferences from './unitDisplay/UnitCheckinPreferences';
import AccessControl from '../../AccessControl';
import permissions from '../../permissionsList';

/**
 * The component for displaying the full information about a unit
 * @param {number} props.match.params.id - The id of the unit to be displayed (passed by React Router route parameter)
 * @param {object} props.location - Data from the history object used for navigation and location
 * @param {string} props.location.pathname - the url of the current page
 * @param {string} props.location.search - any search parameters in the current url
 * @param {string} props.location.previous - The url of the page that called this component. This is used for the "back" links to redirect back to where the user previously was
 * @param {object} props.unit - The unit object being displayed
 * @param {array} props.units - the array of units
 * @param {object} props.parent_unit - the unit object of the parent unit for the current unit being displayed
 * @param {array} props.users - the array of users
 * @returns {object} - The contents of the unit edit form
 */

export function UnconnectedUnitDisplay(props) {
  const location = useLocation();
  const params = useParams();
  const navigate = useNavigate();
  const { search, pathname } = location;
  const searchParams = new URLSearchParams(search);
  const view = searchParams.get('view');

  // React hooks
  const [showArchive, setShowArchive] = useState(false);
  const [showUnArchive, setShowUnArchive] = useState(false);
  const {
    alarmDeviceTypes,
    alertDeviceTypes,
    parent_unit,
    checkinModes,
    units,
    unit,
    users,
  } = props;

  const blankUnit = {
    id: '',
    name: '',
    assigned_device_count: '',
    is_active: true,
    address1: '',
    address2: '',
    city: '',
    state: '',
    zipcode: '',
    path: '',
    parent_unit: '',
    checkin_profile: null,
    alarm_devices: [],
    alert_devices: [],
  };

  let unitObj = blankUnit;
  if (unit) {
    unitObj = unit;
  }

  const currentLocation = pathname + search;
  const currentUnit = params.id;

  let userList = [];
  userList = users.filter((user) => user && user.units && user.units.includes(unitObj.id));

  const unitArray = Object.values(units);
  const unitChildren = unitArray.filter((u) => u.parent_unit === unitObj.id);

  /**
   * Function to load devices from the API to populate the page
   * @fires dispatch#loadAlertDeviceDetails - Redux action to pull device information from the API
   * @fires dispatch#loadAlarmDeviceDetails - Redux action to pull device information from the API
   * @description - This function uses React Hooks to add lifecycle methods. By adding the [] parameter at the end of the hook, it will only fire once. Eslint wants the second paramter to have something in it so it's necessary to add the commented "eslint-disable-line" to prevent it from automatically adding the extra information which will cause the hook to fire every time the component updates.
   */

  useEffect(() => {
    props.dispatch(loadAlarmDeviceTypes());
    props.dispatch(loadAlertDeviceTypes());
    props.dispatch(loadUnit(params.id));
  }, []); // eslint-disable-line

  useEffect(() => {
    props.dispatch(loadUnit(params.id));
  }, [currentUnit]); // eslint-disable-line

  const archiveButtonConfirmationText = (
    <FormattedMessage
      id="unitDisplay.archiveConfirmationText"
      defaultMessage="Yes I want to Archive this unit"
      description="Button to archive the unit"
    />
  );

  const unArchiveButtonConfirmationText = (
    <FormattedMessage
      id="unitDisplay.unArchiveConfirmationText"
      defaultMessage="Yes I want to Unarchive this unit"
      description="Button to unarchive the unit"
    />
  );

  const backText = (
    <FormattedMessage
      id="unitDisplay.backText"
      defaultMessage="Back to Units"
      description="Link back to the units interface"
    />
  );
  const archiveDialogText = (
    <FormattedMessage
      id="unitDisplay.archiveDialogText"
      defaultMessage="Please be aware:{br} Archiving a unit is similar to deleting it,
       doing so will remove it from view and it will no longer be
      accessible. This will also remove all device and user associations to this unit."
      values={{
        br: <br />,
      }}
      description="Text that displays when archiving a unit to warn the user what will happen when the unit is archived"
    />
  );
  const unArchiveDialogText = (
    <FormattedMessage
      id="unitDisplay.unArchiveDialogText"
      defaultMessage="Unarchiving a unit is making a unit active, doing so will add it to the view and it will be accessible."
      values={{
        br: <br />,
      }}
      description="Text that displays when unarchiving a unit to warn the user what will happen when the unit is unarchived"
    />
  );

  function handleArchive() {
    props.dispatch(archiveUnit(unitObj, navigate));
  }

  function handleUnArchive() {
    props.dispatch(unArchiveUnit(unitObj, navigate));
  }

  function archiveConfirmation() {
    setShowArchive(true);
  }

  function cancelArchive() {
    setShowArchive(false);
  }

  function unArchiveConfirmation() {
    setShowUnArchive(true);
  }

  function cancelUnArchive() {
    setShowUnArchive(false);
  }

  /**
   * Function to display the archive challenge notification modal
   */
  function displayArchive() {
    if (showArchive) {
      return (
        <Overlay
          rootUrl={`/units/view/${unitObj.id}`}
          canClose={false}
          data-test="unit-archive-challenge-overlay"
          useFunction={true}
          onClick={cancelArchive}>
          <div
            className="content-container content-container--column"
            data-test="archive-overlay">
            <h1 className="no-margin">
              {archiveChallengeText} {unitObj.name}?
            </h1>
            {archiveDialogText}
            <hr />
            {unitChildren.length > 0 ? (
              <React.Fragment>
                <span className="text-alert text-bold">
                  <FormattedMessage
                    id="unitDisplay.archiveChildUnitsChallenge"
                    defaultMessage="This unit currently has child units associated with it. As a result, it can not be archived until the child units have been moved to a new parent unit or to not have a parent unit any more."
                    description="The parent unit of the unit being created"
                  />
                </span>
                <hr />
              </React.Fragment>
            ) : (
              ''
            )}
            <div className="flex-end width-100">
              <Button
                data-test="cancel-archive"
                onClick={cancelArchive}
                text={cancelText}
                buttonType={buttonTypes.noButton}
                buttonSize={buttonSizes.small}
                buttonTextSize={buttonTextSizes.mediumSmall}
                additionalStyles="no-margin margin-right-15 js-cancel-archive-button"
              />
              <Button
                data-test="submit-archive"
                onClick={handleArchive}
                text={archiveButtonConfirmationText}
                buttonType={buttonTypes.danger}
                buttonSize={buttonSizes.small}
                buttonTextSize={buttonTextSizes.mediumSmall}
                additionalStyles="no-margin  js-submit-archive-button"
                disabled={unitChildren.length > 0}
              />
            </div>
          </div>
        </Overlay>
      );
    } else {
      return '';
    }
  }

  /**
   * Function to display the unarchive challenge notification modal
   */
  function displayUnArchive() {
    if (showUnArchive) {
      return (
        <Overlay
          rootUrl={`/units/view/${unitObj.id}`}
          canClose={false}
          data-test="unit-unarchive-challenge-overlay"
          useFunction={true}
          onClick={cancelArchive}>
          <div className="content-container content-container--column">
            <h1 className="no-margin">
              {unArchiveChallengeText} {unitObj.name}?
            </h1>
            {unArchiveDialogText}
            <hr />
            <div className="flex-end">
              <Button
                data-test="cancel-unarchive"
                onClick={cancelUnArchive}
                text={cancelText}
                buttonType={buttonTypes.noButton}
                buttonSize={buttonSizes.small}
                buttonTextSize={buttonTextSizes.mediumSmall}
                additionalStyles="no-margin margin-right-15"
              />
              <Button
                data-test="submit-unarchive"
                onClick={handleUnArchive}
                text={unArchiveButtonConfirmationText}
                buttonType={buttonTypes.success}
                buttonSize={buttonSizes.small}
                buttonTextSize={buttonTextSizes.mediumSmall}
                additionalStyles="no-margin"
              />
            </div>
          </div>
        </Overlay>
      );
    } else {
      return '';
    }
  }

  function pathDisplay() {
    const pathArray = unitObj.path.split(',');
    const pathLength = pathArray.length;
    if (pathLength > 1) {
      const pathLinks = pathArray.map((pathUnit, index) => {// eslint-disable-line
        const pathUnitInt = parseInt(pathUnit);
        const unitInfo = units[pathUnitInt];
        const pathUnitTrimmed = pathUnit.trim();
        if (unitInfo) {
          const unitName = unitInfo.name;
          if (index === pathLength - 1) {
            const unitTitle = (
              <span
                data-test="path-unit"
                key={pathUnitTrimmed}
                className="link-text align-center margin-right-5 text-bold">
                {unitName}
              </span>
            );
            return unitTitle;
          } else {
            const unitLink = (
              <React.Fragment key={pathUnitTrimmed}>
                <Link
                  data-test="path-link"
                  className="link-text align-center margin-right-5 link-text-deep-blue"
                  to={'/units/' + pathUnitTrimmed + '/view/'}>
                  {unitName}
                </Link>
                {index === pathLength - 1 ? (
                  ''
                ) : (
                  <span className="margin-right-5">{'>'}</span>
                )}
              </React.Fragment>
            );

            return unitLink;
          }
        }
      });
      return pathLinks;
    } else {
      return '';
    }
  }

  function displayInfo() {
    switch (view) {
      case 'devices':
        return (
          <UnitDevices
            data-test="devices-component"
            unit={unitObj}
            currentLocation={currentLocation}
            alertDeviceList={unitObj.alert_devices}
            alarmDeviceList={unitObj.alarm_devices}
            alarmDeviceTypes={alarmDeviceTypes}
            alertDeviceTypes={alertDeviceTypes}
          />
        );
      case 'checkin':
        return (
          <UnitCheckinPreferences
            data-test="checkin-component"
            checkInPreferences={unitObj.checkin_profile}
            currentLocation={currentLocation}
            checkinModes={checkinModes}
            unitObj={unitObj}
          />
        );

      default:
        return (
          <UnitInfo
            data-test="info-component"
            unitInfo={unitObj}
            unitChildren={unitChildren}
            parent_unit={parent_unit}
            userList={userList}
            currentLocation={currentLocation}
          />
        );
    }
  }

  return (
    <div className="container">
      <FormattedMessage
        id="unitdisplay.browserTabInfo"
        defaultMessage="Units | {unitName}"
        description="Done button"
        values={{
          unitName: unitObj.name,
        }}>
        {(txt) => (
          <Helmet>
            <title>{txt}</title>
          </Helmet>
        )}
      </FormattedMessage>
      <div
        className="layout-container content-container content-container--column content-container--shadow"
        data-test="display-container">
        <div className="flex-around width-100 margin-bottom-10">
          <Link
            className="link-text align-center"
            to={'/units/?path=' + unitObj.path}
            data-test="unit-full-view-cancel-link">
            <SvgUpArrowIcon className="rotate-90-ccw margin-right-10" />
            {backText}
          </Link>
        </div>
        <h1 className="no-margin text-center" data-test="display-title">
          {unitObj.name}
        </h1>

        <div
          className="width-100 margin-bottom-10 flex-start"
          data-test="path-container">
          {pathDisplay()}
        </div>
        <div className="tab-bar" data-test="tab-bar">
          <Link
            to={`/units/${unitObj.id}/view/`}
            className={
              view === null
                ? 'text-medium tab-bar__tab tab-bar__tab--active'
                : 'text-medium  tab-bar__tab'
            }
            data-test="info-tab">
            {infoText}
          </Link>

          {unitObj.checkin_profile &&
          Object.values(unitObj.checkin_profile).length > 0 ? (
            <Link
              to={`/units/${unitObj.id}/view/?view=checkin`}
              className={
                view === 'checkin'
                  ? 'text-medium tab-bar__tab tab-bar__tab--active'
                  : 'text-medium  tab-bar__tab'
              }
              data-test="checkin-tab">
              {checkinText}
            </Link>
          ) : (
            ''
          )}
          {unitObj.alarm_devices || unitObj.alert_devices ? (
            <Link
              to={`/units/${unitObj.id}/view/?view=devices`}
              className={
                view === 'devices'
                  ? 'text-medium tab-bar__tab tab-bar__tab--active'
                  : 'text-medium  tab-bar__tab'
              }
              data-test="devices-tab">
              {devicesText}
            </Link>
          ) : (
            ''
          )}
        </div>
        {displayInfo()}

        <div className="flex-spread width-100">
          <AccessControl
            data-test="access-control"
            feature={permissions.static.units.editUnit}>
            {unitObj.is_active ? (
              <Button
                onClick={archiveConfirmation}
                text={archiveText}
                buttonType={buttonTypes.danger}
                buttonSize={buttonSizes.small}
                buttonTextSize={buttonTextSizes.mediumSmall}
                additionalStyles="no-margin js-archive-button"
                data-test="unit-full-view-archive-button"
              />
            ) : (
              <Button
                onClick={unArchiveConfirmation}
                text={unArchiveText}
                buttonType={buttonTypes.success}
                buttonSize={buttonSizes.small}
                buttonTextSize={buttonTextSizes.mediumSmall}
                additionalStyles="no-margin  js-unarchive-button"
                data-test="unit-full-view-unarchive-button"
              />
            )}
          </AccessControl>

          <div className="flex-end align-center">
            <AccessControl
              data-test="access-control"
              feature={permissions.static.units.editUnit}>
              {unitObj.is_active ? (
                <Link
                  data-test="unit-edit-button"
                  className="link-button button-secondary button-sm text-medium-small margin-left-15"
                  to={`/units/${unitObj.id}/edit/`}
                  state={{
                    modal: true,
                    previous: currentLocation,
                  }}>
                  {editText}
                </Link>
              ) : (
                ''
              )}
            </AccessControl>
          </div>
        </div>
        <div className="flex-around width-100 margin-top-10">
          <Link
            className="link-text align-center"
            to={unitObj.is_active ? '/units/?path=' + unitObj.path : '/units/'}
            data-test="unit-full-view-cancel-link">
            <SvgUpArrowIcon className="rotate-90-ccw margin-right-10" />
            {backText}
          </Link>
        </div>
        {unitObj.is_active ? displayArchive() : displayUnArchive()}
      </div>
    </div>
  );
}

const mapStateToProps = (state, ownProps) => {
  // Get id from the component's params prop passed from the wrapper
  const id = ownProps.id;
  const unit = state.units.units[id];
  const units = state.units.units;
  let parent_unit = {};
  if (unit) {
    parent_unit = state.units.units[unit.parent_unit];
  }
  const users = Object.values(state.users.users);

  const alertDevices = state.devices.alertDeviceDetails;
  const alarmDevices = state.devices.alarmDeviceDetails;
  const checkinModes = state.users.checkinModes;

  return {
    unit,
    units,
    parent_unit,
    users,
    alarmDeviceTypes: state.devices.alarmDeviceTypes,
    alertDeviceTypes: state.devices.alertDeviceTypes,
    alertDevices,
    alarmDevices,
    checkinModes,
  };
};

const ConnectedUnitDisplay = connect(mapStateToProps)(UnconnectedUnitDisplay);

// Wrapper component to provide params to the connected component
function UnitDisplayWrapper() {
  const params = useParams();
  return <ConnectedUnitDisplay id={params.id} />;
}

export default UnitDisplayWrapper;
